import { useEffect, useMemo, useState } from 'react';
import {
  Container, Typography, Box, Button, Paper, List, ListItemButton, ListItemText,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormGroup,
  FormControlLabel, Checkbox, Divider, Chip, Stack, Alert, CircularProgress
} from '@mui/material';
import {
  getRoles, getPermissionCatalog, getRolePermissions, createRole, setRolePermissions, deleteRole
} from '../../api/saasApi';
import { useMe } from '../../context/MeContext';
import { ConfirmDialog } from '../../components/ui';

const Roles = () => {
  const { can } = useMe();
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedPerms, setSelectedPerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // role awaiting delete confirm
  const [deleting, setDeleting] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPerms, setNewPerms] = useState([]);

  const canManage = can('role:manage');

  const groups = useMemo(() => {
    const g = {};
    catalog.forEach((p) => { (g[p.group || 'other'] ||= []).push(p); });
    return g;
  }, [catalog]);

  const loadRoles = async () => {
    try {
      const [r, c] = await Promise.all([getRoles(), getPermissionCatalog()]);
      setRoles(r.data?.data || []);
      setCatalog(c.data?.data || []);
    } catch (e) {
      setError('Failed to load roles. You may not have permission.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadRoles(); }, []);

  const openRole = async (role) => {
    setSelected(role);
    const res = await getRolePermissions(role.id);
    setSelectedPerms(res.data?.data || []);
  };

  const togglePerm = (id, list, setList) =>
    setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const saveSelected = async () => {
    await setRolePermissions(selected.id, selectedPerms);
    setSelected(null);
  };

  const submitCreate = async () => {
    await createRole(newName, newPerms);
    setCreateOpen(false); setNewName(''); setNewPerms([]);
    loadRoles();
  };

  const removeRole = (role) => setDeleteTarget(role);

  const confirmRemoveRole = async () => {
    const role = deleteTarget;
    if (!role) return;
    setDeleting(true);
    try {
      await deleteRole(role.id);
      if (selected?.id === role.id) setSelected(null);
      setDeleteTarget(null);
      loadRoles();
    } catch (e) {
      setError(e?.response?.data?.messages?.[0] || 'Could not delete the role.');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={700}>Roles &amp; Permissions</Typography>
        {canManage && <Button variant="contained" onClick={() => setCreateOpen(true)}>New role</Button>}
      </Stack>
      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Paper sx={{ width: 280, p: 1 }}>
          <List>
            {roles.map((role) => (
              <ListItemButton key={role.id} selected={selected?.id === role.id} onClick={() => openRole(role)}>
                <ListItemText primary={role.name} secondary={role.isSystem ? 'System' : 'Custom'} />
                {!role.isSystem && canManage && (
                  <Button size="small" color="error" onClick={(e) => { e.stopPropagation(); removeRole(role); }}>Delete</Button>
                )}
              </ListItemButton>
            ))}
          </List>
        </Paper>

        <Paper sx={{ flex: 1, minWidth: 320, p: 2 }}>
          {!selected ? (
            <Typography color="text.secondary">Select a role to view or edit its permissions.</Typography>
          ) : (
            <>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">{selected.name} {selected.isSystem && <Chip size="small" label="System" sx={{ ml: 1 }} />}</Typography>
                {canManage && <Button variant="contained" onClick={saveSelected}>Save</Button>}
              </Stack>
              <Divider sx={{ my: 2 }} />
              {Object.entries(groups).map(([group, perms]) => (
                <Box key={group} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ textTransform: 'capitalize', color: 'primary.main' }}>{group}</Typography>
                  <FormGroup>
                    {perms.map((p) => (
                      <FormControlLabel
                        key={p.id}
                        control={
                          <Checkbox
                            disabled={!canManage}
                            checked={selectedPerms.includes(p.id)}
                            onChange={() => togglePerm(p.id, selectedPerms, setSelectedPerms)}
                          />
                        }
                        label={<span><code>{p.key}</code> — {p.description}</span>}
                      />
                    ))}
                  </FormGroup>
                </Box>
              ))}
            </>
          )}
        </Paper>
      </Box>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create role</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth label="Role name" margin="normal" value={newName} onChange={(e) => setNewName(e.target.value)} />
          {Object.entries(groups).map(([group, perms]) => (
            <Box key={group} sx={{ mt: 1 }}>
              <Typography variant="subtitle2" sx={{ textTransform: 'capitalize', color: 'primary.main' }}>{group}</Typography>
              <FormGroup>
                {perms.map((p) => (
                  <FormControlLabel
                    key={p.id}
                    control={<Checkbox checked={newPerms.includes(p.id)} onChange={() => togglePerm(p.id, newPerms, setNewPerms)} />}
                    label={<span><code>{p.key}</code></span>}
                  />
                ))}
              </FormGroup>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!newName} onClick={submitCreate}>Create</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title={`Delete role "${deleteTarget?.name || ''}"?`}
        body="Clinicians who have this role will lose the permissions it grants. This can't be undone."
        confirmLabel="Delete role"
        destructive
        busy={deleting}
        onConfirm={confirmRemoveRole}
        onClose={() => setDeleteTarget(null)}
      />
    </Container>
  );
};

export default Roles;
